import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Clock,
  Paperclip,
  Play,
  Plus,
  Search,
  UserPlus,
  Wrench,
} from "lucide-react";
import { request } from "./FunctionalApp";

const label = (value: string = "") =>
  value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const when = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";
async function json(path: string, init: RequestInit = {}) {
  const response = await request(path, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(label(data.error || "Unable to save"));
  return data;
}
const post = (path: string, body: any = {}) =>
  json(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

export function MaintenanceWorkOrders() {
  const [data, setData] = useState<any>({
      orders: [],
      users: [],
      assets: [],
      canAssign: false,
    }),
    [selected, setSelected] = useState(""),
    [detail, setDetail] = useState<any>(null),
    [query, setQuery] = useState(""),
    [status, setStatus] = useState("ACTIVE"),
    [message, setMessage] = useState(""),
    [creating, setCreating] = useState(false);
  async function load(keep = selected) {
    try {
      const next = await json("/api/maintenance/work-orders");
      setData(next);
      if (keep) setDetail(await json(`/api/maintenance/work-orders/${keep}`));
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const rows = useMemo(
    () =>
      data.orders.filter(
        (x: any) =>
          (status === "ALL" ||
            (status === "ACTIVE"
              ? !["COMPLETED", "CANCELLED"].includes(x.status)
              : x.status === status)) &&
          `${x.number} ${x.title} ${x.asset_name || ""} ${x.discipline}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [data.orders, query, status],
  );
  async function open(workOrderId: string) {
    setSelected(workOrderId);
    try {
      setDetail(await json(`/api/maintenance/work-orders/${workOrderId}`));
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  async function action(path: string, body: any = {}) {
    try {
      const result = await post(path, body);
      setMessage(result.message || "Work order updated");
      await load(selected);
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  if (detail)
    return (
      <WorkOrderDetail
        value={detail}
        users={data.users}
        canAssign={data.canAssign}
        back={() => {
          setDetail(null);
          setSelected("");
        }}
        action={action}
        message={message}
      />
    );
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Work orders</h1>
          <p>Report, claim, repair and sign off maintenance work.</p>
        </div>
        <button onClick={() => setCreating(!creating)}>
          <Plus />
          New work order
        </button>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      {creating ? (
        <NewWorkOrder
          assets={data.assets}
          saved={async (workOrderId) => {
            setCreating(false);
            await load(workOrderId);
            await open(workOrderId);
          }}
        />
      ) : null}
      <section className="func-panel cmms-list">
        <div className="func-table-head">
          <h2>Maintenance queue</h2>
          <div className="cmms-filters">
            <label>
              <Search />
              <input
                placeholder="Number, issue, asset or trade"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="ACTIVE">Active</option>
              <option value="ALL">All</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="TEMPORARY_FIX">Temporary fix</option>
              <option value="AWAITING_SIGNOFF">Awaiting sign-off</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
        </div>
        <div className="cmms-orders">
          {rows.map((w: any) => (
            <button key={w.id} onClick={() => open(w.id)}>
              <span>
                <strong>{w.number}</strong>
                <em className={`cmms-priority p-${w.priority}`}>
                  {label(w.priority)}
                </em>
              </span>
              <span>
                <b>{w.title}</b>
                <small>
                  {w.asset_name || "No asset"} · {label(w.discipline)}
                </small>
              </span>
              <span>
                <em className={`cmms-status s-${w.status}`}>
                  {label(w.status)}
                </em>
                <small>
                  {w.assigned_to_name || w.claimed_by_name || "Unassigned"}
                </small>
              </span>
              <span>
                <small>Reported</small>
                {when(w.reported_at)}
              </span>
            </button>
          ))}
        </div>
        {!rows.length ? (
          <p className="func-empty">No matching work orders.</p>
        ) : null}
      </section>
    </>
  );
}

function NewWorkOrder({
  assets,
  saved,
}: {
  assets: any[];
  saved: (id: string) => void;
}) {
  const [f, setF] = useState({
      title: "",
      description: "",
      priority: "MEDIUM",
      assetId: "",
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await post("/api/maintenance/work-orders", {
        ...f,
        assetId: f.assetId || null,
      });
      saved(result.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="func-panel cmms-create">
      <div className="func-table-head">
        <h2>Report maintenance work</h2>
      </div>
      <form onSubmit={submit}>
        <label>
          Issue title
          <input
            required
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </label>
        <label>
          Asset
          <select
            value={f.assetId}
            onChange={(e) => setF({ ...f, assetId: e.target.value })}
          >
            <option value="">No asset selected</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.path || a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value })}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="PRODUCTION_CAN_RUN">Production can run</option>
            <option value="PRODUCTION_STOPPED">Production stopped</option>
          </select>
        </label>
        <label className="wide">
          Description
          <textarea
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </label>
        {error ? <p className="cmms-error">{error}</p> : null}
        <button disabled={busy}>
          {busy ? "Creating…" : "Create work order"}
        </button>
      </form>
    </section>
  );
}

function WorkOrderDetail({
  value,
  users,
  canAssign,
  back,
  action,
  message,
}: any) {
  const w = value.order,
    [note, setNote] = useState(""),
    [minutes, setMinutes] = useState(""),
    [description, setDescription] = useState(""),
    [checklist, setChecklist] = useState<any>({ items: [], complete: true });
  const canSignoff = Boolean(value.permissions?.canSignoff),
    canComplete = Boolean(value.permissions?.canComplete);
  const run = (suffix: string, body: any = {}) =>
    action(`/api/maintenance/work-orders/${w.id}/${suffix}`, body);
  async function loadChecklist() {
    try {
      setChecklist(await json(`/api/maintenance/work-orders/${w.id}/checklist`));
    } catch {}
  }
  useEffect(() => { loadChecklist(); }, [w.id]);
  async function toggleChecklist(item: any) {
    try {
      await post(`/api/maintenance/work-orders/${w.id}/checklist/${item.id}`, { completed: !item.completed_at });
      await loadChecklist();
    } catch (e: any) { alert(e.message); }
  }
  function prompted(suffix: string, text: string, key = "reason") {
    const answer = prompt(text);
    if (answer) run(suffix, { [key]: answer });
  }
  async function attach(file: File) {
    if (file.size > 2_000_000) return alert("Maximum file size is 2 MB.");
    const reader = new FileReader();
    reader.onload = () =>
      run("attachments", {
        name: file.name,
        mimeType: file.type,
        url: reader.result,
      });
    reader.readAsDataURL(file);
  }
  return (
    <>
      <div className="cmms-detail-head">
        <button className="secondary" onClick={back}>
          <ArrowLeft />
          Queue
        </button>
        <div>
          <span>{w.number}</span>
          <h1>{w.title}</h1>
          <p>
            {w.asset_name || "No asset"} · {label(w.discipline)} · Reported{" "}
            {when(w.reported_at)}
          </p>
        </div>
        <em className={`cmms-status s-${w.status}`}>{label(w.status)}</em>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <div className="cmms-detail-grid">
        <section className="func-panel cmms-main">
          <h2>Work order</h2>
          <p>{w.description || "No description supplied."}</p>
          <dl>
            <div>
              <dt>Priority</dt>
              <dd>{label(w.priority)}</dd>
            </div>
            <div>
              <dt>Trade</dt>
              <dd>{label(w.discipline)}</dd>
            </div>
            <div>
              <dt>Claimed by</dt>
              <dd>{w.claimed_by_name || "—"}</dd>
            </div>
            <div>
              <dt>Assigned to</dt>
              <dd>{w.assigned_to_name || "—"}</dd>
            </div>
            <div>
              <dt>Labour</dt>
              <dd>
                {value.labour.reduce((n: number, x: any) => n + x.minutes, 0)}{" "}
                minutes
              </dd>
            </div>
            <div>
              <dt>Production restored</dt>
              <dd>{when(w.production_restored_at)}</dd>
            </div>
          </dl>
          {w.temporary_fix ? (
            <div className="cmms-repair">
              <b>Temporary fix</b>
              <p>{w.temporary_fix}</p>
              <small>
                Permanent repair due {when(w.permanent_repair_due_at)}
              </small>
            </div>
          ) : null}
          {w.permanent_repair ? (
            <div className="cmms-repair permanent">
              <b>Permanent repair</b>
              <p>{w.permanent_repair}</p>
            </div>
          ) : null}
          {checklist.items.length ? (
            <div className="cmms-checklist">
              <h2>PPM checklist</h2>
              <p>{checklist.complete ? "All mandatory steps completed." : "Complete every mandatory step before recording the permanent repair."}</p>
              {checklist.items.map((item: any) => (
                <label key={item.id}>
                  <input type="checkbox" checked={Boolean(item.completed_at)} disabled={!canComplete} onChange={() => toggleChecklist(item)} />
                  <span><b>{item.label}</b>{item.completed_at ? <small>Completed by {item.completed_by_name || "engineer"} · {when(item.completed_at)}</small> : <small>Required</small>}</span>
                </label>
              ))}
            </div>
          ) : null}
          <div className="cmms-actions">
            <ActionButtons
              w={w}
              run={run}
              prompted={prompted}
              canComplete={canComplete}
              canSignoff={canSignoff}
            />
          </div>
          {canAssign ? (
            <div className="cmms-assign">
              <label>
                Assign engineer
                <select
                  defaultValue=""
                  onChange={(e) =>
                    e.target.value && run("assign", { userId: e.target.value })
                  }
                >
                  <option value="">Choose…</option>
                  {users.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Add assistant
                <select
                  defaultValue=""
                  onChange={(e) =>
                    e.target.value &&
                    run("assistants", { userId: e.target.value })
                  }
                >
                  <option value="">Choose…</option>
                  {users.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          <div className="cmms-assistants">
            <b>Assistants</b>
            {value.assistants.length ? (
              value.assistants.map((a: any) => <span key={a.id}>{a.name}</span>)
            ) : (
              <span>None</span>
            )}
          </div>
          {canComplete ? (
            <label className="func-button cmms-attach">
              <Paperclip />
              Attach photo or PDF
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) =>
                  e.target.files?.[0] && attach(e.target.files[0])
                }
              />
            </label>
          ) : null}
          <div className="cmms-attachments">
            {value.attachments.map((a: any) => (
              <a
                key={a.id}
                href={a.url}
                download={a.name}
                target="_blank"
                rel="noreferrer"
              >
                {a.name}
              </a>
            ))}
          </div>
        </section>
        <aside>
          <section className="func-panel cmms-entry">
            <h2>Engineer notes</h2>
            {canComplete ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (note) {
                    run("notes", { body: note });
                    setNote("");
                  }
                }}
              >
                <textarea
                  placeholder="Add a factual update…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button>Add note</button>
              </form>
            ) : null}
            {value.notes.map((n: any) => (
              <div key={n.id}>
                <b>{n.author_name}</b>
                <small>{when(n.created_at)}</small>
                <p>{n.body}</p>
              </div>
            ))}
          </section>
          <section className="func-panel cmms-entry">
            <h2>Labour time</h2>
            {canComplete ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (minutes) {
                    run("labour", { minutes: Number(minutes), description });
                    setMinutes("");
                    setDescription("");
                  }
                }}
              >
                <input
                  type="number"
                  min="1"
                  max="1440"
                  placeholder="Minutes"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                />
                <input
                  placeholder="Work carried out"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <button>
                  <Clock />
                  Log time
                </button>
              </form>
            ) : null}
            {value.labour.map((l: any) => (
              <div key={l.id}>
                <b>
                  {l.user_name} · {l.minutes} min
                </b>
                <small>{when(l.created_at)}</small>
                <p>{l.description}</p>
              </div>
            ))}
          </section>
        </aside>
      </div>
      <section className="func-panel cmms-timeline">
        <h2>Audit timeline</h2>
        {value.events.map((e: any) => (
          <div key={e.id}>
            <i />
            <span>
              <b>{label(e.event)}</b>
              <small>
                {e.actor_name} · {when(e.created_at)}
              </small>
            </span>
            <em>
              {e.from_status ? `${label(e.from_status)} → ` : ""}
              {label(e.to_status)}
            </em>
          </div>
        ))}
      </section>
    </>
  );
}

function ActionButtons({ w, run, prompted, canComplete, canSignoff }: any) {
  if (w.status === "OPEN")
    return (
      <button
        onClick={() => {
          const answer = prompt(
            "Job type: enter General, Mechanical or Electrical",
            "Mechanical",
          );
          if (!answer) return;
          const discipline = answer.trim().toUpperCase();
          if (!["GENERAL", "MECHANICAL", "ELECTRICAL"].includes(discipline))
            return alert("Choose General, Mechanical or Electrical.");
          run("claim", { discipline });
        }}
      >
        <UserPlus />
        Claim for myself
      </button>
    );
  if (!canComplete) return null;
  if (["CLAIMED", "ASSIGNED", "PAUSED", "AWAITING_PARTS"].includes(w.status))
    return (
      <button onClick={() => run("start")}>
        <Play />
        Start / resume work
      </button>
    );
  if (w.status === "IN_PROGRESS")
    return (
      <>
        <button
          onClick={() => prompted("pause", "Why is this work being paused?")}
        >
          Pause
        </button>
        <button
          onClick={() => prompted("await-parts", "Which parts are required?")}
        >
          Awaiting parts
        </button>
        <button
          onClick={() => {
            const fix = prompt("Describe the safe temporary fix");
            if (!fix) return;
            const due = prompt("Permanent repair due date (YYYY-MM-DD)");
            if (due)
              run("temporary-fix", {
                temporaryFix: fix,
                permanentRepairDueAt: due,
              });
          }}
        >
          Record temporary fix
        </button>
        <button
          onClick={() =>
            prompted(
              "permanent-repair",
              "Describe the permanent repair",
              "permanentRepair",
            )
          }
        >
          <Wrench />
          Permanent repair complete
        </button>
      </>
    );
  if (w.status === "TEMPORARY_FIX")
    return (
      <button
        onClick={() =>
          prompted(
            "permanent-repair",
            "Describe the permanent repair",
            "permanentRepair",
          )
        }
      >
        <Wrench />
        Permanent repair complete
      </button>
    );
  if (w.status === "AWAITING_SIGNOFF" && canSignoff)
    return (
      <button
        onClick={() => run("signoff", { comment: "Checked and accepted" })}
      >
        Sign off work
      </button>
    );
  if (w.status === "COMPLETED" && canSignoff)
    return (
      <button
        onClick={() =>
          prompted("reopen", "Why must this completed work order be reopened?")
        }
      >
        Reopen
      </button>
    );
  return null;
}
